/*----------------------------------
- MARKET MENU CATALOG (SSOT)
-----------------------------------*/

import type { IconName } from '@uniquedomains/components/ui/icon';
import { EDITION_PAGES, EDITION_PUBLISH_LABEL } from '@uniquedomains/components/contracts/editions/public';
import { DOMAINING_NEWS_INDEX_PATH } from '@uniquedomains/components/contracts/news/public';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';

/*----------------------------------
- TYPES
-----------------------------------*/

/**
 * A header Market row. Same two-state shape as the Tools catalog: a row either links
 * to a page that exists (`href`) or announces itself as unbuilt (`status`), never both.
 *
 * `fact` is the short note on the right of a desktop row. It is optional on purpose:
 * only a row whose figure is a fact the product can stand behind carries one. The
 * mockup's "1.31M on record", "41 .ai closed this week" and "Next: 14 Oct" are design
 * placeholders for surfaces that do not exist, so they are not in this catalog.
 */
export type LandingMarketMenuItem = {
    id: string;
    label: string;
    description: string;
    iconName: IconName;
    fact?: string;
} & (
    | { href: string; status?: undefined }
    | { href?: undefined; status: 'Coming soon' }
);

/*----------------------------------
- CATALOG
-----------------------------------*/

export const LANDING_MARKET_MENU_ITEMS: readonly LandingMarketMenuItem[] = [
    {
        id: 'sales',
        label: 'Sales',
        description: 'Reported domain sales, by price, ending and word, updated each night.',
        iconName: 'dollar',
        // The rollup runs once a night, a cadence a reader can check against the page's own stamp.
        fact: 'Updated each night',
        href: LANDING_WEBSITE_PAGE_ROUTES.sales,
    },
    {
        id: 'auctions',
        label: 'Auctions',
        description: 'One-word names at auction on seven venues, closing today.',
        iconName: 'coins',
        // The sample is drawn once a UTC day (ADR-0076), a cadence a reader can check on the page.
        fact: 'A new sample each day',
        href: LANDING_WEBSITE_PAGE_ROUTES.auctions,
    },
    {
        id: 'news',
        label: 'News',
        description: 'The industry’s day in one summary, with links to the original reporting.',
        iconName: 'feed',
        // The archive publishes one edition a day, so this states the cadence a reader
        // can check against the archive itself rather than a count.
        fact: 'New edition each morning',
        href: DOMAINING_NEWS_INDEX_PATH,
    },
    {
        id: 'tlds',
        label: 'TLDs',
        description: 'Registry status, prices, sales and launch dates: one page per public-set TLD, the agenda for the rest.',
        iconName: 'globe',
        href: LANDING_WEBSITE_PAGE_ROUTES.tldHub,
    },
    // The registrar hub (ADR-0079): the price lists are read every morning, a cadence the page dates.
    {
        id: 'registrars',
        label: 'Registrars',
        description: 'Year 1, renewal and transfer-in prices at nine registrars, ending by ending.',
        iconName: 'tag',
        fact: 'Prices read each morning',
        href: LANDING_WEBSITE_PAGE_ROUTES.registrars,
    },
    // The daily editions (ADR-0074) replace "Trends" (coming soon) and "Opportunities".
    {
        id: 'trending-domains',
        label: EDITION_PAGES.investor.name,
        description: 'The trends rising in search today and the one-word names tied to them.',
        iconName: 'trending-up',
        fact: `New edition at ${EDITION_PUBLISH_LABEL}`,
        href: LANDING_WEBSITE_PAGE_ROUTES.trendingDomains,
    },
    {
        id: 'business-ideas',
        label: EDITION_PAGES.founder.name,
        description: 'Three businesses to build on each trending one-word name, with its cost.',
        iconName: 'sparkles',
        fact: `New edition at ${EDITION_PUBLISH_LABEL}`,
        href: LANDING_WEBSITE_PAGE_ROUTES.businessIdeas,
    },
    {
        id: 'agenda',
        label: 'Agenda',
        description: 'Launch dates, registry changes, conferences and policy dates.',
        iconName: 'calendar',
        href: LANDING_WEBSITE_PAGE_ROUTES.agenda,
    },
] as const;

/**
 * The pages the Market trigger lights up on, derived from the rows that have one, so
 * an unbuilt row can never light the bar on a page it does not own.
 */
export const LANDING_MARKET_MENU_ACTIVE_PATH_PREFIXES: readonly string[] = LANDING_MARKET_MENU_ITEMS.reduce<
    string[]
>((paths, item) => (item.href ? [...paths, item.href] : paths), []);
